/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ISchemaFlowNodeModel, ISchemaFlowNodeTypeDefinitionMap } from "@adapptio/lib-blueprint";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { Node, NodeProps, Position } from "@adapptio/vendor-react-flow-renderer";
import React from "react";

import { useBlueprintModel } from "../../hooks/useBlueprintModel";
import { Icon, ICON_SIZE } from "../Icon";
import { FlowEditorHandle } from "./FlowEditorHandle";
import { FLOW_EDITOR_LAYOUT, INodeInfo, TGetNodeInfoFn } from "./FlowEditorTypes";

export interface IFlowEditorNodeData {
	model: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>;
	isStartNode: boolean;
	layoutType: FLOW_EDITOR_LAYOUT;
	getNodeInfo?: TGetNodeInfoFn<ISchemaFlowNodeTypeDefinitionMap>;
}

export interface IFlowEditorNodeProps extends NodeProps<IFlowEditorNodeData> { }

export type TFlowEditorNode = Node<IFlowEditorNodeData>;

export const FlowEditorNode: React.FC<IFlowEditorNodeProps> = ({ data, selected }) => {
	const t = useTranslate();
	const { model } = useBlueprintModel(data.model);
	const { model: varNameModel } = useBlueprintModel(data.model?.varName);
	useBlueprintModel(data.model?.opts?.template);

	const nodeType = model.schema.opts.nodeTypes[model.type];
	const outputDescriptors = nodeType.resolveOutputs(model);

	const customInfo: INodeInfo = data.getNodeInfo ? data.getNodeInfo(data.model) : {};

	const typeLabel =
		customInfo.typeLabel !== undefined && customInfo.typeLabel !== null
			? customInfo.typeLabel
			: t("editor", nodeType.label || String(model.type));

	const elementLabel = customInfo.elementLabel;

	const subLabel =
		customInfo.subLabel !== undefined && customInfo.subLabel !== null
			? customInfo.subLabel
			: nodeType.resolveLabel
				? nodeType.resolveLabel(model)
				: null;

	const comment = model.comment.value
		? model.comment.value.split("\n")[0]
		: '';

	const icon = customInfo.icon !== undefined && customInfo.icon !== null ? customInfo.icon : nodeType.icon;

	const outputHandles: React.ReactElement[] = [];
	const outputCount = Object.keys(model.outputs.props).length;
	let outputIndex = 0;

	const baseClass = BEM("edt-flow-editor", "node", {
		"is-selected": selected,
		layout: data.layoutType || FLOW_EDITOR_LAYOUT.HORIZONTAL,
		"was-executed": !!model.executionResult
	});

	for (const k in model.outputs.props) {
		const handleStyle =
			data.layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL
				? {
					top: 10 + (80 / (outputCount + 1)) * (outputIndex + 1) + "%"
				}
				: {
					left: 10 + (80 / (outputCount + 1)) * (outputIndex + 1) + "%"
				};

		outputHandles.push(
			<FlowEditorHandle
				key={k}
				type="source"
				position={
					data.layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? Position.Right : Position.Bottom
				}
				id={k}
				tooltip={t("editor", outputDescriptors[k].label)}
				icon={outputDescriptors[k].icon}
				className={baseClass
					.element("node-handle", {
						"was-activated": model.executionResult?.activeOutput === k
					})
					.toClassName()}
				style={handleStyle}
			/>
		);

		outputIndex++;
	}

	return (
		<div className={baseClass.toClassName()}>
			{!data.isStartNode ? (
				<FlowEditorHandle
					type="target"
					position={
						data.layoutType === FLOW_EDITOR_LAYOUT.HORIZONTAL ? Position.Left : Position.Top
					}
					className={baseClass.element("node-handle").toClassName()}
				/>
			) : null}
			<div className={baseClass.element("node-wrapper").toClassName()}>
				<div className={baseClass.element("node-header").toClassName()}>
					{icon ? <Icon size={ICON_SIZE.CUSTOM} name={icon} /> : null}
					<span className={baseClass.element("node-type").toClassName()}>
						{splitLabelIntoSpans(
							typeLabel,
							baseClass.element("node-element").toClassName()
						)}
						{elementLabel && (
							<>
								<span className={baseClass.element("node-element-separator").toClassName()}>/</span>
								{splitLabelIntoSpans(
									elementLabel,
									baseClass.element("node-element").toClassName()
								)}
							</>
						)}
					</span>
				</div>
				{!!subLabel && (
					<span className={baseClass.element("node-label").toClassName()}>{subLabel}</span>
				)}
				{!!varNameModel?.value && (
					<span className={baseClass.element("node-var-name").toClassName()}>
						${varNameModel.value}
					</span>
				)}
				{!!comment && (
					<span className={baseClass.element("node-comment").toClassName()}>{comment}</span>
				)}
			</div>
			{model.executionResult ? (
				<span className={baseClass.element("node-flag-was-executed").toClassName()} />
			) : null}
			{outputHandles}
		</div>
	);
};

function splitLabelIntoSpans(label: string, className: string) {
	const parts = label.split(".");

	return (
		<>
			{parts.map((part, i) => (
				<span key={i} className={className}>{ i > 0 ? "." : ""}{part}</span>
			))}
		</>
	);
}